import { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Search, TrendingUp, RefreshCw } from 'lucide-react';
import toast from 'react-hot-toast';
import { useSocialStore } from '@/stores/socialStore';
import { FeedCard } from '@/components/discover/FeedCard';
import { ShopThisLook } from '@/components/discover/ShopThisLook';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import type { FeedItem, PurchaseLink } from '@/types';

export function DiscoverPage() {
  const navigate = useNavigate();
  const {
    feed,
    trending,
    isLoadingFeed,
    hasFeedMore,
    fetchFeed,
    fetchTrending,
    like,
    unlike,
    save,
    unsave,
  } = useSocialStore();

  const [shopLinks, setShopLinks] = useState<PurchaseLink[]>([]);
  const [showShop, setShowShop] = useState(false);
  const sentinelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    fetchFeed(true);
    fetchTrending();
  }, [fetchFeed, fetchTrending]);

  // Infinite scroll
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasFeedMore && !isLoadingFeed) {
          fetchFeed();
        }
      },
      { rootMargin: '300px' },
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasFeedMore, isLoadingFeed, fetchFeed]);

  const handleLike = useCallback(
    (item: FeedItem) => {
      if (item.isLiked) unlike(item.id);
      else like(item.id);
    },
    [like, unlike],
  );

  const handleSave = useCallback(
    (item: FeedItem) => {
      if (item.isSaved) unsave(item.id);
      else save(item.id);
    },
    [save, unsave],
  );

  const handleShop = useCallback((item: FeedItem) => {
    setShopLinks(item.purchaseLinks);
    setShowShop(true);
  }, []);

  const handleProfile = useCallback(
    (userId: string) => {
      navigate(`/discover/profile/${userId}`);
    },
    [navigate],
  );

  const handleShare = useCallback((item: FeedItem) => {
    if (navigator.share) {
      navigator.share({ title: item.caption || 'Check out this outfit', url: window.location.href }).catch(() => {});
    } else {
      navigator.clipboard.writeText(window.location.href).then(() => toast('Link copied!'));
    }
  }, []);

  const handleComment = useCallback((_item: FeedItem) => {
    toast('Comments coming soon');
  }, []);

  const handleMore = useCallback((_item: FeedItem) => {
    toast('More options coming soon');
  }, []);

  return (
    <motion.div
      className="min-h-screen bg-vestir-white pb-24"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      {/* Header */}
      <div className="sticky top-0 z-30 bg-vestir-white/90 backdrop-blur-xl border-b border-vestir-gray/30">
        <div className="px-4 pt-12 pb-3 flex items-center justify-between">
          <h1 className="font-display text-2xl font-bold text-vestir-black">
            Discover
          </h1>
          <button
            onClick={() => navigate('/discover/search')}
            className="w-10 h-10 bg-vestir-gray/40 rounded-full flex items-center justify-center"
            aria-label="Search"
          >
            <Search size={20} />
          </button>
        </div>
      </div>

      {/* Trending tags */}
      {trending.length > 0 && (
        <div className="px-4 pt-3 pb-1">
          <div className="flex items-center gap-1.5 mb-2">
            <TrendingUp size={14} className="text-vestir-coral" />
            <span className="text-xs font-medium text-vestir-gray-dark">Trending</span>
          </div>
          <div className="flex gap-2 overflow-x-auto scrollbar-none">
            {trending.map((tag) => (
              <button
                key={tag.tag}
                onClick={() => navigate(`/discover/search?q=${encodeURIComponent(tag.tag)}`)}
                className="flex items-center gap-2 px-3 py-2 bg-white rounded-xl shadow-card
                           flex-shrink-0 hover:shadow-card-hover transition-shadow"
              >
                {tag.thumbnailUrl && (
                  <img
                    src={tag.thumbnailUrl}
                    alt=""
                    className="w-8 h-8 rounded-lg object-cover"
                  />
                )}
                <div className="text-left">
                  <p className="text-xs font-medium">#{tag.tag}</p>
                  <p className="text-[10px] text-vestir-gray-dark">
                    {tag.count.toLocaleString()} posts
                  </p>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Feed */}
      <div className="px-4 pt-3 space-y-4">
        {feed.map((item) => (
          <FeedCard
            key={item.id}
            item={item}
            onLike={handleLike}
            onSave={handleSave}
            onShop={handleShop}
            onProfile={handleProfile}
            onShare={handleShare}
            onComment={handleComment}
            onMore={handleMore}
          />
        ))}

        {/* Sentinel for infinite scroll */}
        <div ref={sentinelRef} className="h-1" />

        {isLoadingFeed && (
          <div className="py-6">
            <LoadingSpinner size="sm" message="Loading more..." />
          </div>
        )}

        {!isLoadingFeed && feed.length === 0 && (
          <div className="text-center py-16">
            <div className="w-16 h-16 bg-vestir-purple/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <RefreshCw size={24} className="text-vestir-purple" />
            </div>
            <p className="text-sm text-vestir-gray-dark">
              Follow people to see their outfits here
            </p>
          </div>
        )}

        {!hasFeedMore && feed.length > 0 && (
          <p className="text-center text-xs text-vestir-gray-dark py-4">
            You are all caught up!
          </p>
        )}
      </div>

      {/* Shop overlay */}
      <AnimatePresence>
        {showShop && (
          <ShopThisLook
            links={shopLinks}
            onClose={() => setShowShop(false)}
          />
        )}
      </AnimatePresence>
    </motion.div>
  );
}
